/**
 * Landing Page — עמוד הבית
 *
 * The public front door. Five doors into the app: the advisory AI, the school
 * programme, retirement planning, mortgages, and the actuarial calculator
 * (מאזן — the one-time-paid איזון משאבים report).
 *
 * Shown to EVERYONE, signed in or not — a signed-in user gets the same four
 * cards plus a shortcut to their own area, because the whole point of the page is
 * that the four areas are discoverable from one place.
 *
 * Each card resolves its destination from auth state, so a signed-in student
 * clicking "פורטל סטודנטים" lands in the portal rather than on a login form.
 */

import { Link } from 'react-router-dom'
import { MessageSquare, ArrowLeft, Calculator } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { TRACKS, MAZAN } from '../config/tracks'

interface DoorCard {
  key: string
  title: string
  description: string
  to: string
  Icon: typeof MessageSquare
  /** Tailwind classes must be literal strings — see topicIcons.ts for why. */
  iconWrap: string
  border: string
  ring: string
}

export default function LandingPage() {
  const { isAuthenticated, user } = useAuthStore()
  const isStudent = user?.role === 'student'
  const isStaff = user?.role === 'admin' || user?.role === 'advisor'

  const cards: DoorCard[] = [
    {
      key: 'advisor',
      title: 'AI יועץ השקעות',
      description: 'ייעוץ אישי מבוסס פרופיל הסיכון שלך, בליווי יפתח ונגר.',
      // Students are confined to their own area; sending them to /chat would
      // just bounce them back, so they get the school portal instead.
      to: isAuthenticated ? (isStudent ? TRACKS.student.basePath : '/chat') : '/login',
      Icon: MessageSquare,
      iconWrap: 'bg-blue-100 text-blue-800',
      border: 'border-blue-200 hover:border-blue-400',
      ring: 'focus-visible:ring-blue-500',
    },
    {
      key: 'student',
      title: TRACKS.student.name,
      description: 'מסלול למידה מובנה להיכרות עם עולם ההשקעות, לתלמידים ולסטודנטים.',
      to: isStudent || isStaff ? TRACKS.student.basePath : TRACKS.student.loginPath,
      Icon: TRACKS.student.Icon,
      iconWrap: 'bg-purple-100 text-purple-800',
      border: 'border-purple-200 hover:border-purple-400',
      ring: 'focus-visible:ring-purple-500',
    },
    {
      key: 'retirement',
      title: TRACKS.retirement.name,
      description: 'להבין את הפנסיה, גיל הפרישה והקצבה — לפני שמחליטים. חינם.',
      to: TRACKS.retirement.basePath,
      Icon: TRACKS.retirement.Icon,
      iconWrap: 'bg-emerald-100 text-emerald-800',
      border: 'border-emerald-200 hover:border-emerald-400',
      ring: 'focus-visible:ring-emerald-500',
    },
    {
      key: 'mortgage',
      title: TRACKS.mortgage.name,
      description: 'מסלולים, ריביות, הון עצמי ויחס החזר — בשפה פשוטה. חינם.',
      to: TRACKS.mortgage.basePath,
      Icon: TRACKS.mortgage.Icon,
      iconWrap: 'bg-amber-100 text-amber-900',
      border: 'border-amber-200 hover:border-amber-400',
      ring: 'focus-visible:ring-amber-500',
    },
    {
      key: 'mazan',
      title: 'מחשבון אקטואריה',
      description: 'תחשיב איזון משאבים לפנסיה בגירושין — דוח מקצועי עם פסיקתות, מוכן להגשה.',
      // /Mazan shows its landing page to visitors and "my reports" to signed-in users.
      to: MAZAN.basePath,
      Icon: Calculator,
      iconWrap: 'bg-teal-100 text-teal-900',
      border: 'border-teal-200 hover:border-teal-400',
      ring: 'focus-visible:ring-teal-500',
    },
  ]

  // min-h-full, not min-h-screen: the page sits inside PublicLayout's flex
  // column above the footer, so a full viewport here would push the footer
  // off-screen and force a scrollbar on an otherwise short page.
  return (
    <div className="min-h-full bg-gradient-to-b from-primary-50 via-white to-white">
      <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:py-16">
        {/* Masthead */}
        <header className="text-center">
          <p className="text-sm font-medium text-primary-800">
            יפתח ונגר — ייעוץ השקעות ופנסיוני אובייקטיבי
          </p>
          <h1 className="mt-3 font-heading text-3xl font-bold text-gray-900 sm:text-4xl">
            במה נוכל לעזור?
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-gray-700 sm:text-lg">
            חמישה אזורים שמסבירים בשפה פשוטה — עם עוזרי AI ייעודיים ומחשבון אקטואריה.
            בחר/י את מה שרלוונטי לך עכשיו.
          </p>

          {isAuthenticated && (
            <p className="mt-5">
              <Link
                to={isStudent ? TRACKS.student.basePath : '/dashboard'}
                className="btn btn-primary inline-flex"
              >
                {user?.firstName ? `שלום ${user.firstName} — לאזור האישי` : 'לאזור האישי'}
                <ArrowLeft size={16} aria-hidden="true" className="ms-1" />
              </Link>
            </p>
          )}
        </header>

        {/* The five doors */}
        <nav className="mt-10 sm:mt-14" aria-label="שערי כניסה">
          <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {cards.map((card) => (
              <li key={card.key}>
                <Link
                  to={card.to}
                  aria-label={`${card.title} — ${card.description}`}
                  className={`flex h-full flex-col rounded-2xl border-2 bg-white p-6 text-right transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${card.border} ${card.ring}`}
                >
                  <span className={`inline-flex w-fit rounded-xl p-3 ${card.iconWrap}`}>
                    <card.Icon size={26} aria-hidden="true" />
                  </span>
                  <h2 className="mt-4 text-lg font-bold text-gray-900">{card.title}</h2>
                  <p className="mt-2 flex-1 text-sm text-gray-700">{card.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
                    כניסה
                    <ArrowLeft size={15} aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {!isAuthenticated && (
          <p className="mt-10 text-center text-sm text-gray-700">
            אין לך חשבון עדיין?{' '}
            <Link to="/register" className="font-medium text-primary-700 underline">
              הרשמה לייעוץ אישי
            </Link>{' '}
            — או היכנס/י חינם לאזורי{' '}
            <Link to={TRACKS.retirement.basePath} className="font-medium text-primary-700 underline">
              תכנון הפרישה
            </Link>{' '}
            ו
            <Link to={TRACKS.mortgage.basePath} className="font-medium text-primary-700 underline">
              המשכנתא
            </Link>
            , או לחשב איזון משאבים ב
            <Link to={MAZAN.basePath} className="font-medium text-primary-700 underline">
              מחשבון האקטואריה
            </Link>
            .
          </p>
        )}

        <p className="mx-auto mt-12 max-w-3xl text-center text-xs leading-relaxed text-gray-600">
          האמור באתר זה הוא מידע כללי בלבד ואינו מהווה ייעוץ השקעות, ייעוץ פנסיוני או
          המלצה לפעולה כלשהי. ייעוץ אישי ניתן על ידי יפתח ונגר, יועץ השקעות ויועץ
          פנסיוני אובייקטיבי, בכפוף להסכם ייעוץ ולשאלון התאמה.
        </p>
      </div>
    </div>
  )
}
